// Public SSE helpers for the streaming-first Quality Compute API.
// Provider-native replay state is deliberately excluded from UI state: it may
// contain opaque signed thinking blocks needed for a later continuation.

export function sanitizePublicEvent(event) {
  if (!event || typeof event !== 'object') return event;
  const sanitized = { ...event };
  delete sanitized.provider_state;
  delete sanitized.raw_provider_messages;
  return sanitized;
}

export function applyPublicStreamEvent(state, rawEvent) {
  const event = sanitizePublicEvent(rawEvent);
  if (!event || typeof event !== 'object') return state;

  if (event.type === 'response.output_text.delta' || event.type === 'message.delta') {
    const delta = event.delta || event.text || '';
    return { ...state, outputText: `${state.outputText || ''}${delta}`, events: [...state.events, event] };
  }
  if (event.type === 'response.thinking.delta') {
    const delta = event.delta || '';
    return {
      ...state,
      thinkingUpdates: `${state.thinkingUpdates || ''}${delta}`,
      events: [...state.events, event],
    };
  }
  if (event.type === 'response.completed') {
    return {
      ...state,
      outputText: typeof event.output_text === 'string' ? event.output_text : state.outputText,
      completed: event,
      events: [...state.events, event],
    };
  }
  if (event.type === 'response.error') {
    return { ...state, error: event.error || 'Provider stream failed', events: [...state.events, event] };
  }
  return { ...state, events: [...state.events, event] };
}

function parseFrame(frame) {
  const data = frame
    .split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n');
  if (!data || data === '[DONE]') return null;
  try {
    return JSON.parse(data);
  } catch (error) {
    throw new Error(`Invalid SSE data: ${error.message}`);
  }
}

export async function* readResponseStream(response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `API call failed (${response.status})`);
  }
  if (!response.body || !response.body.getReader) {
    throw new Error('Streaming response body is unavailable');
  }

  const reader = response.body.getReader();
  const decoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null;
  const decode = (value, stream) => {
    if (decoder) return decoder.decode(value || new Uint8Array(), { stream });
    if (typeof Buffer !== 'undefined') return Buffer.from(value || []).toString('utf8');
    return Array.from(value || [], (byte) => String.fromCharCode(byte)).join('');
  };
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decode(value, !done);
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() || '';
      for (const frame of frames) {
        const event = parseFrame(frame);
        if (event) yield event;
      }
      if (done) break;
    }
    if (buffer.trim()) {
      const event = parseFrame(buffer);
      if (event) yield event;
    }
  } finally {
    reader.releaseLock();
  }
}

export function initialPublicStreamState() {
  return { outputText: '', thinkingUpdates: '', completed: null, error: null, events: [] };
}
